import { useThrottledCallback } from "@/hooks";
import {
    Button,
    Checkbox,
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@heroui/react";
import { IconChevronDown } from "@tabler/icons-react";
import { type FC, useEffect, useMemo, useState } from "react";
import { MonthRangeCalendar } from "./month-range-picker__calendar";

export interface MonthRangePickerProps {
    /* Start date of the range */
    startValue?: Date;
    /* End date of the range */
    endValue?: Date;
    /* Whether the end date is set to current month */
    isCurrent?: boolean;
    /* Minimum allowed date */
    minStartValue?: Date;
    /* Maximum allowed date */
    maxEndValue?: Date;
    /* Show a checkbox to set end date to current date */
    showCurrentCheckbox?: boolean;
    /* Callback when values change */
    onChange?: (startValue: Date, endValue: Date) => void;
    onCurrentChange?: (isCurrent: boolean) => void;
}

// Helper function to normalize dates to first day of month (timezone-safe)
const normalizeToFirstOfMonth = (date: Date): Date => {
    // Create a new date using UTC values to avoid timezone issues
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), 1, 12, 0, 0));
};

// Helper function to check if a date is current month (timezone-safe)
const isCurrentMonth = (date: Date): boolean => {
    const now = new Date();
    return (
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear()
    );
};

export const MonthRangePicker: FC<MonthRangePickerProps> = ({
    startValue: defaultStartValue = new Date(),
    endValue: defaultEndValue = new Date(),
    isCurrent: defaultIsCurrent = false,
    minStartValue,
    maxEndValue,
    showCurrentCheckbox = false,
    onChange,
    onCurrentChange,
}) => {
    // Use the provided dates or default to current date
    const initialStart = normalizeToFirstOfMonth(defaultStartValue);
    const initialEnd = normalizeToFirstOfMonth(defaultEndValue);

    const [startValue, setStartValue] = useState<Date>(initialStart);
    const [endValue, setEndValue] = useState<Date>(initialEnd);
    const [isCurrent, setIsCurrent] = useState(defaultIsCurrent);
    const [startVisible, setStartVisible] = useState<boolean>(false);
    const [endVisible, setEndVisible] = useState<boolean>(false);

    const isInvalid = useMemo(() => {
        return startValue > endValue;
    }, [startValue, endValue]);

    // Handlers
    const handleStartChange = (date: Date, closePopover = true) => {
        // Create a timezone-safe date for the first of the selected month
        const normalizedDate = normalizeToFirstOfMonth(date);
        setStartValue(normalizedDate);

        if (closePopover) {
            setStartVisible(false);
        }
    };

    const handleEndChange = (date: Date, closePopover = true) => {
        // Create a timezone-safe date for the first of the selected month
        const normalizedDate = normalizeToFirstOfMonth(date);
        setEndValue(normalizedDate);
        setIsCurrent(isCurrentMonth(normalizedDate));

        if (closePopover) {
            setEndVisible(false);
        }
    };

    /**
     * We throttle the current change handler because HeroUIs checkbox component calls
     * the onChange/onValueChange handler twice https://github.com/heroui-inc/heroui/issues/5158
     */
    const handleCurrentChange = useThrottledCallback((checked: boolean) => {
        setIsCurrent(checked);

        if (onCurrentChange) {
            onCurrentChange(checked);
        }

        if (checked) {
            // Set to current month (timezone-safe)
            const currentDate = normalizeToFirstOfMonth(new Date());
            setEndValue(currentDate);
        }
    }, 500);

    // Effect to call onChange when values change
    useEffect(() => {
        if (onChange) {
            onChange(startValue, endValue);
        }
    }, [startValue, endValue, onChange]);

    // Format the display dates (only show month and year)
    const formatDate = (date: Date) => {
        // Format the date in a timezone-safe way
        return new Date(date.getTime()).toLocaleDateString("de-DE", {
            month: "short",
            year: "numeric",
            timeZone: "UTC",
        });
    };

    return (
        <section className="grid grid-cols-1 xs:grid-cols-[1fr,auto] items-center justify-center gap-3">
            <div className="flex items-center gap-3">
                <span>Von</span>
                <Popover
                    placement="bottom-start"
                    isOpen={startVisible}
                    onOpenChange={setStartVisible}
                >
                    <PopoverTrigger>
                        <Button
                            variant="bordered"
                            endContent={<IconChevronDown stroke={1.5} />}
                        >
                            {formatDate(startValue)}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent>
                        <MonthRangeCalendar
                            selectedMonth={startValue}
                            onMonthSelect={handleStartChange}
                            minDate={minStartValue}
                            maxDate={endValue}
                            centerYearSelect={false}
                            yearSelectClassName="w-[5.3rem]"
                        />
                    </PopoverContent>
                </Popover>
            </div>
            <div className="flex items-center gap-3">
                <span className="min-w-7 xs:min-w-0">Bis</span>
                <Popover
                    placement="bottom-start"
                    isOpen={endVisible}
                    onOpenChange={setEndVisible}
                >
                    <PopoverTrigger>
                        <Button
                            variant="bordered"
                            isDisabled={isCurrent}
                            endContent={<IconChevronDown stroke={1.5} />}
                        >
                            {formatDate(endValue)}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent>
                        <div className="flex flex-col gap-4">
                            <MonthRangeCalendar
                                isDisabled={isCurrent}
                                selectedMonth={endValue}
                                onMonthSelect={handleEndChange}
                                minDate={startValue}
                                maxDate={maxEndValue}
                                centerYearSelect={false}
                                yearSelectClassName="w-[5.3rem]"
                            />
                        </div>
                    </PopoverContent>
                </Popover>
                {showCurrentCheckbox && (
                    <div className="flex justify-center">
                        <Checkbox
                            isSelected={isCurrent}
                            onValueChange={handleCurrentChange}
                            size="md"
                        >
                            <span className="text-sm">Bis heute</span>
                        </Checkbox>
                    </div>
                )}
            </div>

            {isInvalid && (
                <span className="text-danger text-xs">
                    Start date must be before end date
                </span>
            )}
        </section>
    );
};
